
import { useState } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

interface EstimateItem {
  id: string;
  title: string;
  description: string;
  cost: number;
  days: number;
  features: string[];
}

const estimateItems: EstimateItem[] = [
  {
    id: 'intake-forms',
    title: 'Standard Intake Forms',
    description: 'Automated client onboarding and data collection forms',
    cost: 1800,
    days: 7,
    features: [
      'Custom form design',
      'Data validation',
      'Email notifications',
      'Basic integrations'
    ]
  },
  {
    id: 'crm-integration',
    title: 'CRM Integration',
    description: 'Connect and sync data with your existing CRM system',
    cost: 2400,
    days: 12,
    features: [
      'CRM platform connection',
      'Data synchronization',
      'Field mapping',
      'Real-time updates'
    ]
  },
  {
    id: 'approval-workflow',
    title: 'Multi-Step Approval Workflow',
    description: 'Automated approval processes with multiple stakeholders',
    cost: 4200,
    days: 18,
    features: [
      'Custom approval chains',
      'Conditional routing',
      'Status tracking',
      'Escalation rules'
    ]
  },
  {
    id: 'messaging-notifications',
    title: 'Automated Messaging / Notifications',
    description: 'Smart communication system for clients and team',
    cost: 3000,
    days: 10,
    features: [
      'Email automation',
      'SMS notifications',
      'Template management',
      'Trigger-based messaging'
    ]
  },
  {
    id: 'dashboards-reporting',
    title: 'Custom Dashboards & Reporting',
    description: 'Real-time insights and performance analytics',
    cost: 5000,
    days: 20,
    features: [
      'Interactive dashboards',
      'Custom reports',
      'Data visualization',
      'Export capabilities'
    ]
  },
  {
    id: 'multi-platform-integration',
    title: 'Multi-Platform Integration',
    description: 'Connect Salesforce, Google Sheets, Make.com and more',
    cost: 6000,
    days: 25,
    features: [
      'Salesforce integration',
      'Google Workspace sync',
      'Make.com automation',
      'API connections'
    ]
  },
  {
    id: 'operational-rebuild',
    title: 'Full Operational Rebuild',
    description: 'Complete enterprise-level workflow transformation',
    cost: 10250, // Average of $8,500-$12,000
    days: 37.5, // Average of 30-45 days
    features: [
      'Complete process audit',
      'Custom architecture',
      'Enterprise integrations',
      'Change management'
    ]
  }
];

const EstimatePage = () => {
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  const handleItemToggle = (itemId: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const calculateTotals = () => {
    const selectedItemsData = estimateItems.filter(item => selectedItems.has(item.id));
    let totalCost = 0;
    let totalDays = 0;
    
    selectedItemsData.forEach(item => {
      if (item.id === 'operational-rebuild') {
        // For operational rebuild, use the range values
        totalCost += item.cost; // This is the average (10250)
        totalDays += item.days; // This is the average (37.5)
      } else {
        totalCost += item.cost;
        totalDays += item.days;
      }
    });
    
    // Special handling for operational rebuild in ranges
    const hasOperationalRebuild = selectedItems.has('operational-rebuild');
    
    if (hasOperationalRebuild) {
      // Adjust cost range for operational rebuild
      const otherItemsCost = selectedItemsData
        .filter(item => item.id !== 'operational-rebuild')
        .reduce((sum, item) => sum + item.cost, 0);
      
      const otherItemsDays = selectedItemsData
        .filter(item => item.id !== 'operational-rebuild')
        .reduce((sum, item) => sum + item.days, 0);
      
      return {
        costRange: {
          min: Math.round((otherItemsCost + 8500) * 0.9),
          max: Math.round((otherItemsCost + 12000) * 1.2)
        },
        timelineRange: {
          min: otherItemsDays + 30,
          max: otherItemsDays + 45 + 10
        }
      };
    }
    
    return {
      costRange: {
        min: Math.round(totalCost * 0.9),
        max: Math.round(totalCost * 1.2)
      },
      timelineRange: {
        min: Math.round(totalDays),
        max: Math.round(totalDays) + 10
      }
    };
  };

  const { costRange, timelineRange } = calculateTotals();

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8F4EF' }}>
      <Navigation />
      
      <main className="pt-8 pb-16">
        {/* Breadcrumbs */}
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-8">
          <nav className="flex items-center space-x-2 text-sm">
            <a 
              href="/" 
              className="transition-colors duration-200 cursor-pointer hover:opacity-80"
              style={{ color: '#7B6655' }}
            >
              Home
            </a>
            <i className="ri-arrow-right-s-line" style={{ color: '#7B6655' }}></i>
            <span style={{ color: '#2B2B2B' }}>Estimate</span>
          </nav>
        </div>

        {/* Hero Section */}
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-16">
          <div className="text-center">
            <h1 
              className="text-4xl lg:text-5xl font-bold mb-6"
              style={{ color: '#2B2B2B', fontFamily: 'Manrope, Inter, sans-serif' }}
            >
              Estimate Builder
            </h1>
            <p 
              className="text-xl lg:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed"
              style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
            >
              Select the workflow automation components you need and get an instant cost and timeline estimate for your project.
            </p>
          </div>
        </div>

        {/* Estimate Components */}
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-16">
          <div className="grid gap-6 lg:gap-8">
            {estimateItems.map((item) => (
              <div 
                key={item.id}
                className={`bg-white rounded-xl p-6 lg:p-8 border-2 transition-all duration-200 cursor-pointer ${
                  selectedItems.has(item.id) ? 'border-opacity-100 shadow-lg' : 'border-opacity-20 hover:border-opacity-40'
                }`}
                style={{ 
                  borderColor: selectedItems.has(item.id) ? '#7B6655' : '#E6DACD',
                  backgroundColor: selectedItems.has(item.id) ? '#FEFCFA' : '#FFFFFF'
                }}
                onClick={() => handleItemToggle(item.id)}
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    <div 
                      className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-200 ${
                        selectedItems.has(item.id) ? 'border-opacity-100' : 'border-opacity-40'
                      }`}
                      style={{ 
                        borderColor: '#7B6655',
                        backgroundColor: selectedItems.has(item.id) ? '#7B6655' : 'transparent'
                      }}
                    >
                      {selectedItems.has(item.id) && (
                        <i className="ri-check-line text-white text-sm"></i>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-4">
                      <div className="lg:flex-1">
                        <h3 
                          className="text-xl lg:text-2xl font-bold mb-2"
                          style={{ color: '#2B2B2B', fontFamily: 'Manrope, Inter, sans-serif' }}
                        >
                          {item.title}
                        </h3>
                        <p 
                          className="text-base lg:text-lg mb-4"
                          style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                        >
                          {item.description}
                        </p>
                      </div>
                      
                      <div className="lg:ml-8 lg:text-right">
                        <div 
                          className="text-2xl font-bold mb-1"
                          style={{ color: '#7B6655', fontFamily: 'Manrope, Inter, sans-serif' }}
                        >
                          {item.id === 'operational-rebuild' ? '$8,500 - $12,000' : `$${item.cost.toLocaleString()}`}
                        </div>
                        <div 
                          className="text-sm"
                          style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                        >
                          {item.id === 'operational-rebuild' ? '30-45 days' : `${item.days} days`}
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {item.features.map((feature, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <i 
                            className="ri-check-line text-sm"
                            style={{ color: '#7B6655' }}
                          ></i>
                          <span 
                            className="text-sm"
                            style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                          >
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Estimate Summary */}
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-16">
          <div className="bg-white rounded-xl p-8 lg:p-12 border-2" style={{ borderColor: '#E6DACD' }}>
            <h2 
              className="text-3xl lg:text-4xl font-bold mb-8 text-center"
              style={{ color: '#2B2B2B', fontFamily: 'Manrope, Inter, sans-serif' }}
            >
              Your Project Estimate
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              <div className="text-center">
                <h3 
                  className="text-xl font-bold mb-4"
                  style={{ color: '#2B2B2B', fontFamily: 'Manrope, Inter, sans-serif' }}
                >
                  Estimated Cost
                </h3>
                <div 
                  className="text-4xl lg:text-5xl font-bold"
                  style={{ color: '#7B6655', fontFamily: 'Manrope, Inter, sans-serif' }}
                >
                  {selectedItems.size > 0 ? (
                    `$${costRange.min.toLocaleString()} - $${costRange.max.toLocaleString()}`
                  ) : (
                    'Select components'
                  )}
                </div>
              </div>
              
              <div className="text-center">
                <h3 
                  className="text-xl font-bold mb-4"
                  style={{ color: '#2B2B2B', fontFamily: 'Manrope, Inter, sans-serif' }}
                >
                  Estimated Timeline
                </h3>
                <div 
                  className="text-4xl lg:text-5xl font-bold"
                  style={{ color: '#7B6655', fontFamily: 'Manrope, Inter, sans-serif' }}
                >
                  {selectedItems.size > 0 ? (
                    `${timelineRange.min} - ${timelineRange.max} days`
                  ) : (
                    'Select components'
                  )}
                </div>
              </div>
            </div>
            
            {selectedItems.size > 0 && (
              <div className="text-center">
                <p 
                  className="text-lg mb-8"
                  style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                >
                  Ready to get started? Schedule a workflow audit to discuss your specific needs and get a detailed proposal.
                </p>
                
                <a
                  href="https://calendly.com/moulderdj/workflow-audit"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-8 py-4 rounded-lg font-semibold text-white transition-all duration-200 whitespace-nowrap cursor-pointer hover:opacity-90 text-lg"
                  style={{ backgroundColor: '#7B6655' }}
                >
                  Schedule Your Workflow Audit
                </a>
              </div>
            )}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default EstimatePage;
